/**
 * InflowPay React SDK v2 - React Components
 * 
 * React components that use the iframe-based SDK v2
 * Same API as the original React SDK for easy migration
 */

import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { CardElement as CardElementClass, CardElementOptions } from '../card-element';
import type { PaymentSDKConfig } from '../payment-sdk';
import { PaymentSDK } from '../payment-sdk';
import type {
  CardElementState,
  CSSProperties,
  Locale,
  PaymentError,
  PaymentResult,
} from '../types';
import { PaymentResultStatus } from '../types';

export interface SDKConfig {
  publicKey: string;
  /** Locale for the payment UI. Defaults to 'en' */
  locale?: Locale;
}

export type {
  CardElementState, PaymentError,
  PaymentResult
} from '../types';

export { PaymentResultErrorCode, PaymentResultStatus } from '../types';

export interface CardElementProps {
  paymentId: string;
  onReady?: () => void;
  onChange?: (state: CardElementState) => void;
  onComplete?: (result: PaymentResult) => void;
  onError?: (error: PaymentError) => void;
  style?: CSSProperties;
  buttonText?: string;
  placeholders?: {
    cardNumber?: string;
    expiry?: string;
    cvc?: string;
  };
  /** Show default success UI after payment (default true). If false, only unmount iframe. */
  showDefaultSuccessUI?: boolean;
}

interface InflowPayContextValue {
  sdk: PaymentSDK;
}

const InflowPayContext = createContext<InflowPayContextValue | null>(null);

/**
 * InflowPayProvider - React component
 * 
 * Same API as the original React SDK
 * 
 * @example
 * ```tsx
 * <InflowPayProvider config={{ apiKey: 'inflow_pub_xxx' }}>
 *   <CardElement
 *     paymentId="pay_xxx"
 *     onComplete={(result) => {
 *       if (result.status === 'CHECKOUT_SUCCESS') {
 *         window.location.href = '/success';
 *       }
 *     }}
 *   />
 * </InflowPayProvider>
 * ```
 */
export function InflowPayProvider({
  config,
  children,
}: {
  config: SDKConfig;
  children: ReactNode;
}) {
  const [sdk] = useState(() => {
    const sdkConfig: PaymentSDKConfig = {
      publicKey: config.publicKey,
      locale: config.locale,
    };
    return new PaymentSDK(sdkConfig);
  });

  return (
    <InflowPayContext.Provider value={{ sdk }}>
      {children}
    </InflowPayContext.Provider>
  );
}

/**
 * useInflowPay - Hook to access InflowPay SDK instance
 * 
 * @example
 * ```tsx
 * function CustomComponent() {
 *   const inflow = useInflowPay();
 *   
 *   const checkStatus = async () => {
 *     const status = await inflow.getPaymentStatus('pay_xxx');
 *     console.log(status);
 *   };
 *   
 *   return <button onClick={checkStatus}>Check Status</button>;
 * }
 * ```
 */
export function useInflowPay(): PaymentSDK {
  const context = useContext(InflowPayContext);
  if (!context) {
    throw new Error('useInflowPay must be used within InflowPayProvider');
  }
  return context.sdk;
}

/**
 * CardElement - React component
 * 
 * Same API as the original React SDK
 * 
 * @example
 * ```tsx
 * <CardElement
 *   paymentId="pay_xxx"
 *   onComplete={(result) => {
 *     if (result.status === 'CHECKOUT_SUCCESS') {
 *       window.location.href = '/success';
 *     }
 *   }}
 * />
 * ```
 */
export function CardElement(props: CardElementProps & { config?: SDKConfig }) {
  const {
    paymentId,
    onReady,
    onChange,
    onComplete,
    onError,
    style,
    buttonText,
    placeholders,
    showDefaultSuccessUI,
    config: propConfig,
  } = props;
  const context = useContext(InflowPayContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardElementRef = useRef<CardElementClass | null>(null);
  const [mounted, setMounted] = useState(false);

  const sdk = context?.sdk || (propConfig ? new PaymentSDK({
    publicKey: propConfig.publicKey,
    locale: propConfig.locale,
  }) : null);

  if (!sdk) {
    throw new Error('CardElement must be used within InflowPayProvider or have a config prop');
  }

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    if (!containerRef.current.id) {
      containerRef.current.id = `inflowpay-card-element-${Date.now()}`;
    }

    const cardElementOptions: CardElementOptions = {
      container: containerRef.current,
      paymentId: paymentId,
      ...(style && { style }),
      ...(buttonText && { buttonText }),
      ...(placeholders && { placeholders }),
      ...(showDefaultSuccessUI !== undefined && { showDefaultSuccessUI }),
      onComplete: (result: PaymentResult) => {
        if (onComplete) {
          onComplete(result);
        }
      },
      onError: (error) => {
        if (onError) {
          onError(error);
        } else if (onComplete) {
          onComplete({
            status: PaymentResultStatus.FAILED,
            paymentId: paymentId,
            error: error,
          });
        }
      },
      onClose: () => {
      },
    };

    const cardElement = sdk.createCardElement(cardElementOptions);
    cardElementRef.current = cardElement;
    cardElement.mount();
    setMounted(true);

    if (onReady) {
      const timer = setTimeout(() => {
        onReady();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [paymentId, sdk, onComplete, onError, onReady, style, buttonText, placeholders, showDefaultSuccessUI]);

  useEffect(() => {
    if (onChange && mounted) {
      onChange({ complete: false });
    }
  }, [mounted, onChange]);

  useEffect(() => {
    return () => {
      if (cardElementRef.current) {
        cardElementRef.current.destroy();
        cardElementRef.current = null;
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: style?.fillParent ? "100%" : "344px"
      }}
    />
  );
}
